<template>
  <div class="park-map" ref="containerRef">
        <span class="park-map__title">{{ park?.name }}</span>
    <button v-if="activeFilter" class="park-map__clear-filter" @click="toggleFilter(activeFilter)">
      <span class="mdi mdi-close-circle-outline"></span> Limpiar filtro
    </button>

    <svg
    ref="svgRef"
    class="park-map__svg"
    :viewBox="viewBoxString"
    @wheel="onWheel"
    @mousedown="onMouseDown"
    @mousemove="onMouseMove"
    @mouseup="onMouseUp"
    @mouseleave="onMouseUp"
    >

    <rect
    :x="BASE_VIEWBOX.x"
    :y="BASE_VIEWBOX.y"
    :width="BASE_VIEWBOX.w"
    :height="BASE_VIEWBOX.h"
    class="park-map__canvas-bg"
  />
      <g v-if="park?.mapBackgroundSvg" v-html="park.mapBackgroundSvg" class="park-map__background"></g>

      <polygon
          v-for="plot in park?.plots || []"
          :key="plot.id"
          :points="polygonToPoints(plot.polygon)"
          :style="{ fill: STATUS_COLORS[plot.status] }"
          class="park-map__plot"
          :class="{ 'park-map__plot--dimmed': isDimmed(plot) }"
          @click="selectPlot(plot)"
          @mouseenter="onPlotHover(plot)"
          @mousemove="onPlotMouseMove"
          @mouseleave="onPlotLeave"
        ></polygon>

        <g
          v-for="plot in ticketedPlots"
          :key="`ticket-badge-${plot.id}`"
          class="park-map__ticket-badge"
          @click="selectPlot(plot)"
          @mouseenter="onPlotHover(plot)"
          @mousemove="onPlotMouseMove"
          @mouseleave="onPlotLeave"
        >
          <circle
            :cx="plotCentroid(plot.polygon).x"
            :cy="plotCentroid(plot.polygon).y"
            r="13"
            :fill="TICKET_PRIORITY_COLORS[plot.topTicketPriority]"
          ></circle>
          <text
            :x="plotCentroid(plot.polygon).x"
            :y="plotCentroid(plot.polygon).y"
            class="park-map__ticket-badge-text"
          >{{ plot.openTicketCount }}</text>
        </g>
    </svg>

      <div
        v-if="hoveredPlot"
        class="park-map__tooltip"
        :style="{ left: tooltipPos.x + 'px', top: tooltipPos.y + 'px' }"
      >
        <span class="park-map__tooltip-name">{{ hoveredPlot.name }}</span>
        <span class="park-map__tooltip-tenant">{{ hoveredPlot.currentTenant?.name || 'Vacante' }}</span>
        <span v-if="hoveredContractEndDate" class="park-map__tooltip-date">
          Vence: {{ hoveredContractEndDate }}
        </span>
      </div>

    <div class="park-map__controls">
      <button class="park-map__control-btn" @click="zoomIn" title="Acercar">
        <span class="mdi mdi-plus"></span>
      </button>
      <button class="park-map__control-btn" @click="zoomOut" title="Alejar">
        <span class="mdi mdi-minus"></span>
      </button>
      <button class="park-map__control-btn" @click="resetView" title="Restablecer vista">
        <span class="mdi mdi-fit-to-page-outline"></span>
      </button>
    </div>

    <div class="park-map__legend">
      <button
        v-for="(label, status) in STATUS_LABELS"
        :key="status"
        class="park-map__legend-item"
        :class="{ 'park-map__legend-item--active': activeFilter === status }"
        @click="toggleFilter(status)"
      >
        <span class="park-map__legend-dot" :style="{ background: STATUS_COLORS[status] }"></span>
        <span class="park-map__legend-label">{{ label }}</span>
        <span class="park-map__legend-count">{{ statusCounts[status] || 0 }}</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed } from 'vue'

const props = defineProps({
  park: { type: Object, default: null },
  activeFilter: { type: String, default: null },
})

const emit = defineEmits(['select-plot', 'update:activeFilter'])

const STATUS_COLORS = {
  OCCUPIED: 'var(--status-occupied)',
  VACANT: 'var(--status-vacant)',
  MAINTENANCE: 'var(--status-maintenance)',
  RESERVED: 'var(--status-reserved)',
}

const STATUS_LABELS = {
  OCCUPIED: 'Ocupada',
  VACANT: 'Vacante',
  MAINTENANCE: 'Mantenimiento',
  RESERVED: 'En negociación',
}

// El filtro vive en el componente padre (para que el nuevo panel de
// distribución de área también pueda controlarlo); aquí solo lo leemos.
const activeFilter = computed(() => props.activeFilter)

const statusCounts = computed(() => {
  const counts = {}
  for (const plot of props.park?.plots || []) {
    counts[plot.status] = (counts[plot.status] || 0) + 1
    // "En mantenimiento" cuenta también las naves con tickets abiertos, con o sin inquilino.
    if (plot.status !== 'MAINTENANCE' && plot.openTicketCount > 0) counts.MAINTENANCE = (counts.MAINTENANCE || 0) + 1
  }
  return counts
})

function toggleFilter(status) {
  emit('update:activeFilter', props.activeFilter === status ? null : status)
}

function isDimmed(plot) {
  if (activeFilter.value === null) return false
  if (activeFilter.value === 'MAINTENANCE') return plot.status !== 'MAINTENANCE' && !(plot.openTicketCount > 0)
  return activeFilter.value !== plot.status
}

function polygonToPoints(polygon) {
  return polygon.map((p) => `${p.x},${p.y}`).join(' ')
}

const TICKET_PRIORITY_COLORS = {
  CRITICAL: '#e84545',
  HIGH: '#f97316',
  MEDIUM: '#f0a500',
  LOW: '#94a3b8',
}

const ticketedPlots = computed(() => (props.park?.plots || []).filter((p) => p.openTicketCount > 0 && p.polygon?.length))

function plotCentroid(polygon) {
  if (!polygon || !polygon.length) return { x: 0, y: 0 }
  const sum = polygon.reduce((acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }), { x: 0, y: 0 })
  return { x: sum.x / polygon.length, y: sum.y / polygon.length }
}

function selectPlot(plot) {
  emit('select-plot', plot)
}

const hoveredPlot = ref(null)
const tooltipPos = reactive({ x: 0, y: 0 })

function onPlotHover(plot) {
  hoveredPlot.value = plot
}

function onPlotMouseMove(e) {
  if (!containerRef.value) return
  const rect = containerRef.value.getBoundingClientRect()
  tooltipPos.x = e.clientX - rect.left + 14
  tooltipPos.y = e.clientY - rect.top + 14
}

function onPlotLeave() {
  hoveredPlot.value = null
}

const hoveredContractEndDate = computed(() => {
  const contract = hoveredPlot.value?.contracts?.[0]
  if (!contract) return null
  return new Date(contract.endDate).toLocaleDateString('es-MX', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
})

// --- Pan & zoom ---
// --- Pan & zoom ---
import { onMounted, onBeforeUnmount } from 'vue'

const FALLBACK_VIEWBOX = { w: 919, h: 684 }
const NATIVE_WIDTH = computed(() => props.park?.mapWidth || FALLBACK_VIEWBOX.w)
const NATIVE_HEIGHT = computed(() => props.park?.mapHeight || FALLBACK_VIEWBOX.h)

const BASE_VIEWBOX = reactive({ x: 0, y: 0, w: NATIVE_WIDTH.value, h: NATIVE_HEIGHT.value })
const viewBox = reactive({ ...BASE_VIEWBOX })
const svgRef = ref(null)
const containerRef = ref(null)
let isPanning = false
let lastPoint = { x: 0, y: 0 }
let resizeObserver = null

const viewBoxString = computed(() => `${viewBox.x} ${viewBox.y} ${viewBox.w} ${viewBox.h}`)

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max)
}

// Ajusta el viewBox base para que su proporción coincida con la del
// contenedor real, agregando margen a los lados o arriba/abajo en vez
// de recortar contenido (slice) o dejar espacio vacío (meet).
function syncBaseViewboxToContainer() {
  if (!containerRef.value) return
  const rect = containerRef.value.getBoundingClientRect()
  if (rect.width === 0 || rect.height === 0) return

  const containerAspect = rect.width / rect.height
  const nativeAspect = NATIVE_WIDTH.value / NATIVE_HEIGHT.value

  let w = NATIVE_WIDTH.value
  let h = NATIVE_HEIGHT.value

  if (containerAspect > nativeAspect) {
    w = h * containerAspect
  } else {
    h = w / containerAspect
  }

  BASE_VIEWBOX.x = -(w - NATIVE_WIDTH.value) / 2
  BASE_VIEWBOX.y = -(h - NATIVE_HEIGHT.value) / 2
  BASE_VIEWBOX.w = w
  BASE_VIEWBOX.h = h

  Object.assign(viewBox, BASE_VIEWBOX)
}

function clampViewBox() {
  const minW = BASE_VIEWBOX.w * 0.15
  const minH = BASE_VIEWBOX.h * 0.15

  viewBox.w = clamp(viewBox.w, minW, BASE_VIEWBOX.w)
  viewBox.h = clamp(viewBox.h, minH, BASE_VIEWBOX.h)

  viewBox.x = clamp(viewBox.x, BASE_VIEWBOX.x, BASE_VIEWBOX.x + BASE_VIEWBOX.w - viewBox.w)
  viewBox.y = clamp(viewBox.y, BASE_VIEWBOX.y, BASE_VIEWBOX.y + BASE_VIEWBOX.h - viewBox.h)
}

function zoomBy(factor) {
  const newW = viewBox.w * factor
  const newH = viewBox.h * factor
  const cx = viewBox.x + viewBox.w / 2
  const cy = viewBox.y + viewBox.h / 2

  viewBox.w = newW
  viewBox.h = newH
  viewBox.x = cx - newW / 2
  viewBox.y = cy - newH / 2

  clampViewBox()
}

function zoomIn() {
  zoomBy(0.85)
}

function zoomOut() {
  zoomBy(1.15)
}

function onWheel(e) {
  e.preventDefault()
  const scaleFactor = e.deltaY > 0 ? 1.1 : 0.9
  const rect = svgRef.value.getBoundingClientRect()
  const mouseXRatio = (e.clientX - rect.left) / rect.width
  const mouseYRatio = (e.clientY - rect.top) / rect.height

  const newW = viewBox.w * scaleFactor
  const newH = viewBox.h * scaleFactor

  viewBox.x -= (newW - viewBox.w) * mouseXRatio
  viewBox.y -= (newH - viewBox.h) * mouseYRatio
  viewBox.w = newW
  viewBox.h = newH

  clampViewBox()
}

function onMouseDown(e) {
  isPanning = true
  lastPoint = { x: e.clientX, y: e.clientY }
}

function onMouseMove(e) {
  if (!isPanning) return
  const rect = svgRef.value.getBoundingClientRect()
  const dx = (e.clientX - lastPoint.x) * (viewBox.w / rect.width)
  const dy = (e.clientY - lastPoint.y) * (viewBox.h / rect.height)
  viewBox.x -= dx
  viewBox.y -= dy
  lastPoint = { x: e.clientX, y: e.clientY }

  clampViewBox()
}

function onMouseUp() {
  isPanning = false
}

function resetView() {
  syncBaseViewboxToContainer()
}

onMounted(() => {
  syncBaseViewboxToContainer()
  resizeObserver = new ResizeObserver(() => {
    syncBaseViewboxToContainer()
  })
  if (containerRef.value) {
    resizeObserver.observe(containerRef.value)
  }
})

onBeforeUnmount(() => {
  if (resizeObserver) {
    resizeObserver.disconnect()
  }
})
</script>

<style scoped>
.park-map {
  position: relative;
  width: 100%;
  height: 100%;
  background: var(--bg-surface-1);
  border: 2px solid var(--border-active);
  border-radius: 10px;
  overflow: hidden;
}

.park-map__title {
  position: absolute;
  top: 1rem;
  left: 1.1rem;
  color: var(--text-content);
  font-family: var(--font-body);
  font-size: 0.85rem;
  font-weight: 600;
  z-index: 1;
  pointer-events: none;
}

.park-map__clear-filter {
  position: absolute;
  top: 2.6rem;
  left: 1.1rem;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  background: var(--bg-surface-1);
  border: 1px solid var(--border-active);
  border-radius: 6px;
  padding: 0.3rem 0.6rem;
  font-family: var(--font-body);
  font-size: 0.75rem;
  color: var(--text-secondary);
  cursor: pointer;
}

.park-map__clear-filter:hover {
  color: var(--accent-red);
  border-color: var(--accent-red);
}

.park-map__svg {
  width: 100%;
  height: 100%;
  display: block;
  cursor: grab;
}

.park-map__svg:active {
  cursor: grabbing;
}

.park-map__background {
  pointer-events: none;
}

.park-map__plot {
  stroke: var(--bg-surface-1);
  stroke-width: 2;
  cursor: pointer;
  transition: opacity 0.15s ease;
}

.park-map__plot:hover {
  opacity: 0.8;
}

.park-map__plot--dimmed {
  opacity: 0.15;
}

.park-map__ticket-badge {
  cursor: pointer;
}

.park-map__ticket-badge circle {
  stroke: #ffffff;
  stroke-width: 2;
}

.park-map__ticket-badge:hover circle {
  stroke-width: 3;
}

.park-map__ticket-badge-text {
  font-family: var(--font-body);
  font-size: 15px;
  font-weight: 700;
  fill: #ffffff;
  text-anchor: middle;
  dominant-baseline: central;
  pointer-events: none;
  user-select: none;
}

.park-map__controls {
  position: absolute;
  top: 1rem;
  right: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.park-map__control-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: rgb(255, 255, 255);
  border: 1px solid var(--border-subtle);
  border-radius: 6px;
  color: var(--text-secondary);
  cursor: pointer;
  font-size: 1rem;
}

.park-map__control-btn:hover {
  color: var(--text-content);
}

.park-map__legend {
  position: absolute;
  bottom: 1rem;
  right: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.6rem;
  min-width: 150px;
  transform: scale(0.8);
  transform-origin: bottom right;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.08);
}

.park-map__legend-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  color: #475569;
  font-size: 0.75rem;
  font-family: var(--font-body);
  padding: 0.25rem 0.4rem;
  border-radius: 4px;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s ease, color 0.15s ease;
}

.park-map__legend-item:hover {
  background: #f1f5f9;
  color: var(--text-content);
}

.park-map__legend-item--active {
  background: #e2e8f0;
  color: var(--text-content);
}

.park-map__legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 20%;
  flex-shrink: 0;
}

.park-map__legend-label {
  flex: 1;
}

.park-map__legend-count {
  font-family: var(--font-mono);
  color: #94a3b8;
}

.park-map__tooltip {
  position: absolute;
  pointer-events: none;
  background: rgba(14, 23, 41, 0.92);
  backdrop-filter: blur(8px);
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  padding: 0.5rem 0.7rem;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  z-index: 5;
  white-space: nowrap;
}

.park-map__tooltip-name {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-primary);
}

.park-map__tooltip-tenant {
  font-size: 0.75rem;
  color: var(--text-secondary);
}

.park-map__tooltip-date {
  font-size: 0.72rem;
  font-family: var(--font-mono);
  color: var(--text-muted);
}
.park-map__canvas-bg {
  fill: var(--map-bg);
  pointer-events: none;
}
</style>